import { useState, useEffect, useRef } from "react";

/* ─────────────────────────────────────────────
   IMAGE IMPORTS — src/assets/images/home/
───────────────────────────────────────────── */
import HeroBg          from "../../assets/images/home/hero-bg.webp";
import AboutLogo       from "../../assets/images/home/about-logo.webp";
import SubscribeBg     from "../../assets/images/home/subscribe-mixing-board.webp";
import AlertBg         from "../../assets/images/home/alert-watercolor-bg.webp";
import AlertBgMobile    from "../../assets/images/home/alert-watercolor-bg-mobile.webp";
import BlogTeslaImg    from "../../assets/images/home/blog-tesla.webp";
import BlogBatteryImg  from "../../assets/images/home/blog-battery-swap.webp";
import BlogOlaImg      from "../../assets/images/home/blog-ola-electric.webp";
import BlogEvTrendsImg from "../../assets/images/home/blog-ev-trends.webp";

/* Episode card background images */
import EpImgPeterVarghese  from "../../assets/images/home/episode-ep62-peter-varghese.webp";
import EpImgMontek         from "../../assets/images/home/episode-ep61-montek-singh.webp";
import EpImgAmitabhKant    from "../../assets/images/home/episode-ep55-amitabh-kant.webp";
import EpImgSudhirMehta    from "../../assets/images/home/episode-ep51-sudhir-mehta.webp";
import EpImgVijay          from "../../assets/images/home/episode-ep25-vijay-vaitheeswaran.webp";
import EpImgRejiKumar      from "../../assets/images/home/episode-ep07-reji-kumar-pillai.webp";

/* ─────────────────────────────────────────────
   ICON IMPORTS — src/assets/icons/home/
───────────────────────────────────────────── */
import IconYoutube      from "../../assets/icons/home/youtube.svg";
import IconSpotify      from "../../assets/icons/home/spotify.svg";
import IconApplePodcast from "../../assets/icons/home/apple-podcast.svg";
import IconPlay         from "../../assets/icons/home/play.svg";
import IconHeadphone    from "../../assets/icons/home/headphone.svg";
import IconArrowRight   from "../../assets/icons/home/arrow-right.svg";

/* ─────────────────────────────────────────────
   PodcastHome.jsx
   Prefix      : pod-
   Fonts       : Montserrat (headings) | Roboto (body)
   BG rule     : sections full width (100%)
   Content rule: max-width 1600px, padding 0 102px
   Breakpoints : 768px tablet | 480px mobile

   SECTION ORDER:
   1. Hero
   2. Episodes Slider
   3. About
   4. Subscribe (parallax)
   5. New Episode Alert (2x2 Spotify grid)
   6. Quote / Watercolor bg
   7. News & Blogs

   LINK RULES:
   - Internal (energizingindia.tv): path only, same tab
   - External (YouTube, Spotify, Apple): full URL, target="_blank"
───────────────────────────────────────────── */

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Roboto:wght@300;400;500;700&display=swap');

  .pod-wrap *,
  .pod-wrap *::before,
  .pod-wrap *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  .pod-wrap {
    font-family: 'Roboto', sans-serif;
    background: #1A1A1A;
    color: #ffffff;
    width: 100%;
    overflow-x: hidden;
  }

  /* ── INNER CONTAINER ── */
  .pod-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
    width: 100%;
  }

  /* ── SCROLL FADE-IN ── */
  .pod-fade-up {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .pod-fade-up.pod-visible {
    opacity: 1;
    transform: translateY(0);
  }
  .pod-fade-up.pod-delay-1 { transition-delay: 0.1s; }
  .pod-fade-up.pod-delay-2 { transition-delay: 0.2s; }
  .pod-fade-up.pod-delay-3 { transition-delay: 0.3s; }
  .pod-fade-up.pod-delay-4 { transition-delay: 0.4s; }
  .pod-fade-up.pod-delay-5 { transition-delay: 0.5s; }

  /* ════════════════════════════════════
     SECTION 1 — HERO
  ════════════════════════════════════ */
  .pod-hero {
    width: 100%;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding-top: 80px;
  }
  .pod-hero-bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center right;
    z-index: 0;
  }
  .pod-hero-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      110deg,
      rgba(5,5,32,0.92)    0%,
      rgba(11,11,53,0.85)  25%,
      rgba(22,8,64,0.75)   45%,
      rgba(45,14,90,0.5)   65%,
      rgba(90,20,128,0.25) 80%,
      transparent          100%
    );
    z-index: 1;
  }
  .pod-hero-glow {
    position: absolute;
    right: -80px;
    top: -60px;
    width: 700px;
    height: 700px;
    background: radial-gradient(
      ellipse at 60% 40%,
      rgba(140,50,200,0.45) 0%,
      rgba(80,20,140,0.2)  40%,
      transparent          70%
    );
    pointer-events: none;
    z-index: 1;
    border-radius: 50%;
  }
  .pod-hero-body {
    flex: 1;
    display: flex;
    align-items: center;
    min-height: 684px;
    position: relative;
    z-index: 3;
    padding-top: 70px;
    padding-bottom: 60px;
  }
  .pod-hero-label {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    opacity: 0;
    animation: podHeroIn 0.8s ease 0.2s forwards;
  }
  .pod-hero-label-line {
    width: 89px;
    height: 1px;
    background: #FFFFFF;
    flex-shrink: 0;
  }
  .pod-hero-label-text {
    font-family: 'Montserrat', sans-serif;
    font-size: 16px;
    color: #FFFFFF;
    letter-spacing: 0.4px;
  }
  .pod-hero-title {
    font-family: 'Montserrat', sans-serif;
    font-size: clamp(32px, 3.6vw, 50px);
    font-weight: 700;
    color: #fff;
    line-height: 140%;
    max-width: 629px;
    margin-bottom: 24px;
    opacity: 0;
    animation: podHeroIn 0.8s ease 0.4s forwards;
  }
  .pod-hero-sub {
    font-family: 'Montserrat', sans-serif;
    font-size: 20px;
    color: #FFFFFF;
    line-height: 140%;
    max-width: 445px;
    opacity: 0;
    animation: podHeroIn 0.8s ease 0.6s forwards;
  }
  @keyframes podHeroIn {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── LISTEN BAR ── */
  .pod-listen-bar {
    width: 100%;
    background: #000000;
    padding: 40px 0;
    position: relative;
    z-index: 3;
  }
  .pod-listen-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }
  .pod-listen-title {
    font-family: 'Montserrat', sans-serif;
    font-size: 24px;
    font-weight: 600;
    color: #fff;
    letter-spacing: 0.3px;
  }
  .pod-listen-logos {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 180px;
    flex-wrap: wrap;
  }
  .pod-platform {
    display: block;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.2s;
    text-decoration: none;
  }
  .pod-platform:hover { opacity: 0.75; transform: translateY(-2px); }
  .pod-platform img {
    height: 36px;
    display: block;
    object-fit: contain;
  }

  /* ════════════════════════════════════
     SECTION 2 — EPISODES SLIDER
  ════════════════════════════════════ */
  .pod-episodes {
    width: 100%;
    background: #1A1A1A;
    padding: 100px 0;
  }
  .pod-ep-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 60px;
  }
  .pod-ep-label {
    font-family: 'Montserrat', sans-serif;
    font-size: 24px;
    font-weight: 700;
    color: #743FAC;
    margin-bottom: 14px;
    letter-spacing: 0.3px;
  }
  .pod-ep-title {
    font-family: 'Montserrat', sans-serif;
    font-size: clamp(26px, 2.8vw, 50px);
    font-weight: 700;
    color: #fff;
    line-height: 140%;
    max-width: 632px;
  }
  .pod-btn-purple {
    background: #743FAC;
    color: #fff;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    padding: 15px 30px;
    border-radius: 5px;
    white-space: nowrap;
    letter-spacing: 0.3px;
    margin-top: 10px;
    transition: background 0.2s, transform 0.15s;
    text-decoration: none;
    display: inline-block;
  }
  .pod-btn-purple:hover { background: #5e2e90; transform: translateY(-2px); }

  .pod-slider-wrap {
    overflow: hidden;
  width: 100%;
  cursor: grab;
  user-select: none;
  touch-action: none;
  }
  .pod-slider-wrap.pod-dragging { cursor: grabbing; }
  .pod-slider-track {
    display: flex;
    gap: 30px;
    will-change: transform;
  }
  .pod-ep-card {
    flex: 0 0 calc((100% - 60px) / 3);
    background: #1A1A1A;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
  }
  .pod-ep-card-img {
    position: relative;
    height: 450px;
    overflow: hidden;
    display: flex;
    align-items: flex-end;
    background-size: cover;
    background-position: center top;
    background-repeat: no-repeat;
  }
  .pod-ep-card-img::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
  }
  .pod-ep-tag {
    position: absolute;
    top: 10px;
    left:50%;
    display: flex;
    align-items: center;
    gap: 6px;
    background: linear-gradient(to right, #3D1E4B 0%, #5F1733 100%);
    border-radius: 10px;
    padding: 10px 71px 10px 71px;
    font-family: 'Roboto', sans-serif;
    font-size: 18px;
    font-weight: 400;
    color: #fff;
    white-space: nowrap;
    z-index: 2;
     transform: translateX(-50%);
  }
  .pod-ep-tag img {
    width: 14px;
    height: 14px;
    object-fit: contain;
    filter: brightness(0) invert(1);
  }
  .pod-ep-namebar {
    position: absolute;
    bottom: 14px;
    left: 14px;
    right: 14px;
    backdrop-filter: blur(6px);
    border-radius: 6px;
    padding: 10px 14px;
    display: none;
    align-items: center;
    gap: 10px;
    z-index: 3;
  }
  .pod-ep-num {
    font-family: 'Montserrat', sans-serif;
    font-size: 12px;
    font-weight: 700;
    color: #fff;
    background: rgba(255,255,255,0.15);
    padding: 4px 8px;
    border-radius: 4px;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .pod-ep-guest-name {
    font-family: 'Montserrat', sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: #fff;
    line-height: 1.3;
  }
  .pod-ep-guest-role {
    font-family: 'Roboto', sans-serif;
    font-size: 11px;
    color: rgba(255,255,255,0.75);
    line-height: 1.3;
    margin-top: 2px;
  }
  .pod-ep-card-bottom {
    padding: 20px 0 0;
    display: flex;
    gap: 14px;
    align-items: flex-start;
  }
  .pod-play-btn {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    margin-top: 2px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: transparent;
   
    transition: background 0.2s, border-color 0.2s;
    text-decoration: none;
  }
  .pod-play-btn:hover {  }
  .pod-ep-desc {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    color: #FFFFFF;
    line-height: 140%;
  }
  .pod-ep-desc-link {
    flex: 1;
    text-decoration: none;
    color: inherit;
    cursor: pointer;
  }
  .pod-ep-desc-link:hover .pod-ep-desc {
    color: #c8a0f0;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  .pod-ep-dots {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 24px;
  }
  .pod-ep-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #3C3C3C;
    cursor: pointer;
    border: none;
    padding: 0;
    transition: background 0.2s, transform 0.2s;
  }
  .pod-ep-dot.pod-active {
    background: #743FAC;
    transform: scale(1.3);
  }

  /* ════════════════════════════════════
     SECTION 3 — ABOUT
  ════════════════════════════════════ */
  .pod-about {
    width: 100%;
    background: #000000;
    padding: 70px 0;
  }
  .pod-about-row {
    display: flex;
    align-items: center;
    gap: 0;
  }
  .pod-about-stats {
    display: flex;
    flex-direction: column;
    gap: 30px;
    flex-shrink: 0;
    width: 150px;
  }
  .pod-stat-box {
    background: linear-gradient(135deg, #000000 0%, #743FAC 100%);
    border-radius: 10px;
    padding: 12px 28.5px;
    width: 150px;
    transition: transform 0.3s;
  }
  .pod-stat-box:hover { transform: translateX(4px); }
  .pod-stat-num {
    font-family: 'Montserrat', sans-serif;
    font-size: 38px;
    font-weight: 700;
    color: #fff;
    line-height: 120%;
  }
  .pod-stat-label {
    font-family: 'Montserrat', sans-serif;
    font-size: 16px;
    color: #fff;
    margin-top: 2px;
  }
  .pod-about-logo-col {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 20px;
  }
  .pod-about-logo-col img {
    max-width: 322px;
    width: 100%;
    object-fit: contain;
    display: block;
    opacity: 0.9;
    transition: opacity 0.3s;
  }
  .pod-about-logo-col img:hover { opacity: 1; }
  .pod-about-text-col {
    flex: 1;
    max-width: 594px;
  }
  .pod-about-eyebrow {
    font-family: 'Montserrat', sans-serif;
    font-size: 24px;
    font-weight: 500;
    color: #743FAC;
    margin-bottom: 14px;
  }
  .pod-about-title {
    font-family: 'Montserrat', sans-serif;
    font-size: clamp(26px, 2.6vw, 50px);
    font-weight: 700;
    color: #fff;
    line-height: 140%;
    margin-bottom: 14px;
  }
  .pod-about-body {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    color: #FFFFFF;
    line-height: 140%;
  }

  /* ════════════════════════════════════
     SECTION 4 — SUBSCRIBE (parallax)
  ════════════════════════════════════ */
  .pod-subscribe {
    width: 100%;
    position: relative;
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    background-attachment: fixed;
  }
  .pod-subscribe-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to right,
      rgba(10,5,20,0.92) 0%,
      rgba(10,5,20,0.72) 45%,
      rgba(10,5,20,0.2)  100%
    );
    z-index: 1;
  }
  .pod-subscribe-content {
    position: relative;
    z-index: 2;
    padding: 80px 0;
  }
  .pod-sub-title {
    font-family: 'Montserrat', sans-serif;
    font-size: clamp(26px, 2.8vw, 50px);
    font-weight: 700;
    color: #fff;
    line-height: 140%;
    margin-bottom: 25px;
    max-width: 500px;
  }
  .pod-sub-form {
    display: flex;
    max-width: 440px;
    gap: 14px;
  }
  .pod-sub-input {
    flex: 1;
    background: rgba(0,0,0,0.5);
    color: #fff;
    border: 1px solid #fff;
    outline: none;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    padding: 14px 18px;
    border-radius: 5px;
  }
  .pod-sub-input::placeholder { color: #fff; }
  .pod-sub-btn {
    background: #743FAC;
    color: #fff;
    border: none;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif;
    font-size: 16px;
    font-weight: 600;
    padding: 16px 30px;
    border-radius: 5px;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: background 0.2s, gap 0.2s;
  }
  .pod-sub-btn:hover { background: #5e2e90; gap: 12px; }
  .pod-sub-btn img {
    width: 16px;
    height: 16px;
    object-fit: contain;
    filter: brightness(0) invert(1);
  }

  /* ════════════════════════════════════
     SECTION 5 — NEW EPISODE ALERT
     2x2 Spotify embed grid
  ════════════════════════════════════ */
  .pod-newalert {
    width: 100%;
    background: #1A1A1A;
    padding: 100px 0;
  }
  .pod-newalert__heading {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    text-align: center;
    margin-bottom: 60px;
    letter-spacing: 1%;
  }
  .pod-newalert__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
  }
  .pod-sp-card {
    border-radius: 14px;
    overflow: hidden;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    border: 1.5px solid #2a2a2a;
    background: #111111;
  }
  .pod-sp-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(116,63,172,0.25);
  }
  .pod-sp-card--highlight {
    border: none;
    // background: #743FAC;
    padding: 3px;
    border-radius: 14px;
  }
  .pod-sp-card--highlight .pod-sp-inner {
    border-radius: 12px;
    overflow: hidden;
  }
  .pod-sp-card iframe {
    display: block;
    width: 100%;
    border: none;
  }

  /* ════════════════════════════════════
     SECTION 6 — QUOTE / WATERCOLOR BG
  ════════════════════════════════════ */
  .pod-quote {
    width: 100%;
    position: relative;
    overflow: hidden;
    min-height: 400px;
    display: flex;
    align-items: center;
  }
  .pod-quote__bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
        object-fit: contain;
    object-position: center;
    z-index: 0;
  }
  .pod-quote__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    z-index: 2;
    width: 100%;
    padding: 74px 0;
  }
  .pod-quote__text {
    font-family: 'Montserrat', sans-serif;
    font-size: clamp(28px, 3vw, 60px);
    font-weight: 700;
    color: #fff;
    line-height: 1.3;
    max-width: 560px;
    margin-left: 223px;
  }
  .pod-quote__person {
    width: 280px;
    height: 280px;
    flex-shrink: 0;
    position: relative;
    overflow: hidden;
    display: none;
  }

  /* ════════════════════════════════════
     SECTION 7 — NEWS & BLOGS
  ════════════════════════════════════ */
  .pod-blogs {
    width: 100%;
    background: #1A1A1A;
    padding: 100px 0 80px;
  }
  .pod-blogs-header {
    text-align: center;
    margin-bottom: 60px;
  }
  .pod-blogs-title {
    font-family: 'Montserrat', sans-serif;
    font-size: clamp(26px, 2.8vw, 50px);
    font-weight: 700;
    color: #fff;
    margin-bottom: 24px;
  }
  .pod-blogs-sub {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    color: #FFFFFF;
    line-height: 140%;
    max-width: 648px;
    margin: 0 auto;
  }
  .pod-blog-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
  }
  .pod-blog-card {
    background: #1A1A1A;
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    align-items: stretch;
    cursor: pointer;
    transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
    border: 1px solid #333333;
    text-decoration: none;
    padding: 20px 0 20px 20px;
  }
  .pod-blog-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 40px rgba(123,47,190,0.2);
    border-color: rgba(155,89,245,0.2);
  }
  .pod-blog-img {
    width: 306px;
    flex-shrink: 0;
    overflow: hidden;
    border-radius: 10px;
    min-height: 160px;
  }
  .pod-blog-img img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.4s ease;
  }
  .pod-blog-card:hover .pod-blog-img img { transform: scale(1.06); }
  .pod-blog-info {
    padding: 0 43px;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .pod-blog-post-title {
    font-family: 'Montserrat', sans-serif;
    font-size: 20px;
    font-weight: 500;
    color: #fff;
    line-height: 1.4;
    margin-bottom: 8px;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  .pod-blog-date {
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    color: #FFFFFF;
    font-weight: 300;
    margin-bottom: 14px;
  }
  .pod-read-more {
    display: inline-flex;
    align-items: start;
    gap: 8px;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #743FAC;
    text-decoration: none;
    transition: gap 0.2s;
  }
  .pod-read-more img {
    width: 22px;
    height: 22px;
    object-fit: contain;
    filter: invert(42%) sepia(80%) saturate(600%) hue-rotate(235deg) brightness(100%);
  }

  /* ════════════════════════════════════
     TABLET — 768px
  ════════════════════════════════════ */
  @media (max-width: 768px) {
    .pod-inner { padding-left: 32px; padding-right: 32px; }
    .pod-hero { padding-top: 0px;}
    .pod-hero-body { min-height: 360px; padding-top: 50px; padding-bottom: 40px; }
    .pod-listen-logos { gap: 60px; }
    .pod-episodes { padding: 50px 0 60px; }
    .pod-ep-top { flex-direction: column; gap: 20px; }
    .pod-ep-card { flex: 0 0 calc((100% - 30px) / 2); }
    .pod-about { padding: 40px 0; }
    .pod-about-row { flex-direction: column; gap: 28px; align-items: center; }
    .pod-about-stats { flex-direction: row; flex-wrap: wrap; width: 100%; gap: 16px; }
    .pod-stat-box { width: calc(50% - 8px); padding: 16px 20px; }
    .pod-stat-num { font-size: 36px; }
    .pod-stat-label { font-size: 15px; margin-top: 4px; }
    .pod-about-logo-col { width: 100%; padding: 0; justify-content: center; }
    .pod-about-logo-col img { max-width: 180px; }
    .pod-about-text-col { max-width: 100%; width: 100%; text-align: center; }
    .pod-about-eyebrow { text-align: center; }
    .pod-about-title { text-align: center; }
    .pod-about-body { text-align: center; }
    .pod-subscribe { background-attachment: scroll; }
    .pod-newalert { padding: 40px 0 40px; }
    .pod-newalert__heading { font-size: 32px; margin-bottom: 32px; }
    .pod-newalert__grid { grid-template-columns: 1fr; gap: 16px; }
    .pod-quote {     min-height: auto; padding-left: 32px;}
    .pod-quote__row { flex-wrap: wrap; gap: 24px; padding: 40px 0; }
    .pod-quote__text { margin-left: 0; }
    .pod-blogs { padding: 40px 0 40px; }
    .pod-blog-grid { grid-template-columns: 1fr; gap: 24px; }
    .pod-blog-img { width: 160px; }
    .pod-blogs-header {    margin-bottom: 32px;}
    .pod-ep-card-img { height: 374px; }
    .pod-ep-tag {padding: 5px 28px 5px 5px;}


  }

  /* ════════════════════════════════════
     MOBILE — 480px
  ════════════════════════════════════ */
  @media (max-width: 480px) {
    .pod-inner { padding-left: 16px; padding-right: 16px; }
    .pod-hero-body { min-height: 300px; padding-top: 22px; padding-bottom: 32px; }
    .pod-hero-label {gap:5px;    margin-bottom: 15px;}
    .pod-hero-label-text {font-size: 14px;}
    .pod-hero-label-line {width: 32px;}
    .pod-hero-sub {font-size: 14px;}
    .pod-hero-title { font-size: 30px;    max-width: 282px ;line-height:120%;     margin-bottom: 15px;}
    .pod-listen-bar {padding: 30px 0 54px;}
       .pod-episodes {padding: 60px 0;}

    .pod-listen-logos { gap: 24px; flex-direction: row; align-items: flex-start; }
.pod-platform img { height: 22px;}
.pod-btn-purple {font-size: 14px;    padding: 12px 20px;text-align:center;margin:0 auto;}
.pod-ep-label {font-size: 16px; text-align:center;}
.pod-ep-top {    margin-bottom: 40px;}
.pod-ep-title { font-size: 30px; text-align:center;line-height: 120%;}
    .pod-ep-top { flex-direction: column; gap: 16px; }
    .pod-ep-card { flex: 0 0 100%; }
    .pod-ep-card-img { height: 374px; }
    .pod-ep-tag {padding: 5px 28px 5px 50px;}
    .pod-ep-card-bottom {padding: 15px 0px 0px 0px;}
    .pod-ep-desc {font-size: 14px;}
.pod-play-btn img {    width: 34px;height:34px; border:none;}
.pod-ep-dots {margin-top:15px;}
    .pod-about {padding :52px 0 40px;}
    .pod-about-stats { flex-direction: row; flex-wrap: wrap; gap: 27px; }
    .pod-about-eyebrow { text-align: center;font-size: 16px; }
    .pod-about-title { text-align: center; font-size: 30px; line-height:120%;}
    .pod-about-body { text-align: center;font-size: 14px;  }
    .pod-subscribe-content { padding: 31px 0 36px;}
    .pod-sub-title { text-align:center; font-size:30px;margin-bottom:37px;}
    .pod-stat-num{font-size:38px;}
    .pod-stat-box { width: calc(46% - 6px);padding: 38px 32px 38px 32px; }
    .pod-sub-form { flex-direction: column; max-width: 100%; }
    .pod-sub-input { border-radius: 5px; }
    .pod-sub-btn { border-radius: 5px; justify-content: center; font-size: 14px;}
    .pod-newalert__heading { font-size: 30px; margin-bottom:40px;}
        .pod-newalert__grid {gap:30px;}
    .pod-quote__text { margin-left: 0; font-size:30px;}
    .pod-quote {     min-height: 222px; padding-left: 0px;}
    .pod-blogs { padding: 60px 0 60px; }
      .pod-blogs-title {font-size:30px;margin-bottom:15px;}  
      .pod-blogs-sub {font-size:14px;}
    .pod-blog-card { flex-direction:row;padding: 15px 0 22px 11px; }
    .pod-blog-info {padding: 0 18px;}
    .pod-blog-img { width: 166px; height: 126px; }
    .pod-blog-post-title {    font-size: 16px;}
    .pod-quote__bg { height:auto}

  }
`;

/* ─────────────────────────────────────────────
   HOOK: useScrollFade
───────────────────────────────────────────── */
function useScrollFade() {
  useEffect(() => {
    const els = document.querySelectorAll(".pod-fade-up");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("pod-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* ─────────────────────────────────────────────
   HOOK: useCounter
───────────────────────────────────────────── */
function useCounter(target, duration = 1800) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const num = parseInt(target.replace(/\D/g, ""), 10);
          const step = Math.ceil(num / (duration / 16));
          let current = 0;
          const timer = setInterval(() => {
            current += step;
            if (current >= num) { current = num; clearInterval(timer); }
            setCount(current);
          }, 16);
        }
      },
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration]);

  return { count, ref };
}

/* ─────────────────────────────────────────────
   SUB: AnimatedStat
───────────────────────────────────────────── */
function AnimatedStat({ num, label }) {
  const suffix = num.replace(/[0-9]/g, "");
  const { count, ref } = useCounter(num);
  return (
    <div ref={ref} className="pod-stat-box pod-fade-up">
      <div className="pod-stat-num">{count}{suffix}</div>
      <div className="pod-stat-label">{label}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SUB: PersonFigure SVG
───────────────────────────────────────────── */
function PersonFigure() {
  return (
    <svg width="220" height="240" viewBox="0 0 220 240"
      xmlns="http://www.w3.org/2000/svg"
      style={{ position: "absolute", bottom: 0 }}>
      <ellipse cx="110" cy="50" rx="38" ry="42" fill="#c8a878" />
      <ellipse cx="110" cy="22" rx="38" ry="18" fill="#1a1a1a" />
      <rect x="72" y="18" width="76" height="20" fill="#1a1a1a" />
      <rect x="98" y="88" width="24" height="20" rx="4" fill="#c8a878" />
      <path d="M55,108 Q80,98 110,105 Q140,98 165,108 L175,240 H45 Z" fill="#1a1a1a" />
      <path d="M90,100 L110,118 L130,100" fill="none" stroke="#333" strokeWidth="3" />
      <path d="M55,110 Q28,135 30,175 L52,178 Q55,150 72,130Z" fill="#1a1a1a" />
      <path d="M165,110 Q190,135 185,175 L162,178 Q162,150 148,130Z" fill="#1a1a1a" />
      <circle cx="148" cy="135" r="5" fill="#cc3333" opacity="0.8" />
    </svg>
  );
}

/* ─────────────────────────────────────────────
   EPISODES DATA (slider)
   Internal links: path only, same tab
───────────────────────────────────────────── */
const EPISODES = [
  {
    ep: "Ep 62", name: "Peter VARGHESE",
    role: "Former Secretary, Dept. of Foreign Affairs & Trade",
    desc: "EP 62 | Peter VARGHESE on China's Tech Dominance, Trump's America, & the India-Australia Partnership.",
    href: "/ep-62-peter-varghese-on-chinas-tech-dominance-trumps-america-the-india-australia-partnership",
    img: EpImgPeterVarghese,
    nameBarColor: "rgba(60,40,140,0.85)",
  },
  {
    ep: "Ep 61", name: "Montek Singh AHLUWALIA",
    role: "Former Deputy Chairman, Planning Commission of India",
    desc: "EP 61 | 1991: India's Near-Default Moment. How Did We Survive — and in 2025 What Challenges Come Next?",
    href: "/ep-61-1991-indias-near-default-moment-how-did-we-survive-and-in-2025-what-challenges-come-next-montek-singh-ahluwalia",
    img: EpImgMontek,
    nameBarColor: "rgba(80,60,160,0.85)",
  },
  {
    ep: "Ep 55", name: "Amitabh KANT",
    role: "India's G20 Sherpa",
    desc: "EP 55 | Inside India's Journey Towards Energy Transition with Amitabh Kant",
    href: "/ep-55-inside-indias-journey-towards-energy-transition-with-amitabh-kant/",
    img: EpImgAmitabhKant,
    nameBarColor: "rgba(120,40,200,0.85)",
  },
  {
    ep: "Ep 51", name: "Sudhir MEHTA",
    role: "Founder & CEO of EKA Pinnacle Industries",
    desc: "EP 51 | Revolutionizing India's Buses: A Startup's Electric Impact | Sudhir Mehta | EKA & Pinnacle Industries",
    href: "/ep-51-revolutionizing-indias-buses-a-startups-electric-impact-sudhir-mehta-eka-pinnacle-industries/",
    img: EpImgSudhirMehta,
    nameBarColor: "rgba(100,30,180,0.85)",
  },
  {
    ep: "Ep 25", name: "Vijay VAITHEESWARAN",
    role: "Global Energy & Climate Innovation Editor, The Economist",
    desc: "EP 25 | Decoding China's Dominance on EVs Globally | The Economist | Vijay VAITHEESWARAN",
    href: "/ep-25-decoding-chinas-dominance-on-evs-globally-the-economist-vijay-vaitheeswaran/",
    img: EpImgVijay,
    nameBarColor: "rgba(80,30,140,0.85)",
  },
  {
    ep: "Ep 07", name: "Reji Kumar PILLAI",
    role: "President, ISGF",
    desc: "E07 | Candid Conversation with Reji Kumar PILLAI, President of India Smart Grid Forum on charging standards in India.",
    href: "/e07-candid-conversation-with-reji-kumar-pillai-president-of-india-smart-grid-forum-on-charging-standards-in-india-the-power-grids-ability-to-accommodate-a-massive-move-to-e-mobility/",
    img: EpImgRejiKumar,
    nameBarColor: "rgba(60,20,100,0.85)",
  },
];

/* ─────────────────────────────────────────────
   SPOTIFY EPISODES DATA (2x2 grid)
   pos 0 top-left    : default
   pos 1 top-right   : highlight purple
   pos 2 bottom-left : highlight purple
   pos 3 bottom-right: default
───────────────────────────────────────────── */
const SPOTIFY_EPISODES = [
  { id: "7HfFi80XmM9ROhhjvBX3nW", highlight: false },
  { id: "6qw1cHzfZJdrmPh3j0UAW9", highlight: true  },
  { id: "0qI0ST6j30Acu2RUEL4LvB", highlight: true  },
  { id: "53GGJdS0x06pXo1AP2yBHp", highlight: false },
];

/* ─────────────────────────────────────────────
   BLOGS DATA
   Internal links: path only, same tab
───────────────────────────────────────────── */
const BLOGS = [
  { title: "What Happened With Tesla India?",                            date: "October 31, 2022", img: BlogTeslaImg,    href: "/blog/what-happened-with-tesla-india" },
  { title: "Is Battery Swapping a viable solution for India?",           date: "June 8, 2022",     img: BlogBatteryImg,  href: "/blog/is-battery-swapping-a-viable-solution-for-india" },
  { title: "Is OLA Electric's ambitions for EVs in India flawed?",       date: "March 15, 2022",   img: BlogOlaImg,      href: "/blog/is-ola-electrics-ambitions-for-evs-in-india-flawed/" },
  { title: "Electric Vehicle Trends In India & Top Selling EVs in 2021", date: "June 8, 2022",     img: BlogEvTrendsImg, href: "/blog/electric-vehicle-trends-in-india-and-top-selling-manufactures-in-2021/" },
];

/* ─────────────────────────────────────────────
   SUB: EpisodeCard
   Internal link — no target, same tab
   Play icon + desc text both link to episode
───────────────────────────────────────────── */
function EpisodeCard({ ep, name, role, desc, href, img, nameBarColor }) {
  return (
    <div className="pod-ep-card">
      <div className="pod-ep-card-img" style={{ backgroundImage: `url(${img})` }}>
        <div className="pod-ep-tag">
          <img src={IconHeadphone} alt="" />
          EV, Podcast, Top 6 episodes
        </div>
        <div className="pod-ep-namebar" style={{ background: nameBarColor || "rgba(100,30,180,0.85)" }}>
          <span className="pod-ep-num">{ep}</span>
          <div>
            <div className="pod-ep-guest-name">{name}</div>
            <div className="pod-ep-guest-role">{role}</div>
          </div>
        </div>
      </div>
      <div className="pod-ep-card-bottom">
        <a href={href} className="pod-play-btn" aria-label="Play episode">
          <img src={IconPlay} alt="Play" />
        </a>
        <a href={href} className="pod-ep-desc-link">
          <p className="pod-ep-desc">{desc}</p>
        </a>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SUB: SpotifyCard (2x2 grid)
───────────────────────────────────────────── */
function SpotifyCard({ id, highlight, index }) {
  const embedUrl = `https://open.spotify.com/embed/episode/${id}?utm_source=generator&theme=0`;
  const delayClass = `pod-delay-${index + 1}`;

  if (highlight) {
    return (
      <div className={`pod-sp-card pod-sp-card--highlight pod-fade-up ${delayClass}`}>
        <div className="pod-sp-inner">
          <iframe
            src={embedUrl}
            width="100%"
            height="152"
            frameBorder="0"
            allowFullScreen
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            title={`Energizing India Episode ${id}`}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`pod-sp-card pod-fade-up ${delayClass}`}>
      <iframe
        src={embedUrl}
        width="100%"
        height="152"
        frameBorder="0"
        allowFullScreen
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
        title={`Energizing India Episode ${id}`}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────
   SUB: BlogCard
   Internal link — no target, same tab
───────────────────────────────────────────── */
function BlogCard({ title, date, img, href }) {
  return (
    <a href={href} className="pod-blog-card">
      <div className="pod-blog-img">
        <img src={img} alt={title} />
      </div>
      <div className="pod-blog-info">
        <div>
          <p className="pod-blog-post-title">{title}</p>
          <p className="pod-blog-date">{date}</p>
        </div>
        <span className="pod-read-more">
          Read More
          <img src={IconArrowRight} alt="" />
        </span>
      </div>
    </a>
  );
}

/* ─────────────────────────────────────────────
   SECTION 1: HERO
───────────────────────────────────────────── */
function Hero() {
  return (
    <section className="pod-hero">
      <img src={HeroBg} alt="" className="pod-hero-bg" aria-hidden="true" />
      <div className="pod-hero-overlay" />
      <div className="pod-hero-glow" />
      <div className="pod-hero-body">
        <div className="pod-inner">
          <div className="pod-hero-label">
            <div className="pod-hero-label-line" />
            <span className="pod-hero-label-text">Podcasting experience</span>
          </div>
          <h1 className="pod-hero-title">
            Where big ideas meet binge-worthy episodes
          </h1>
          <p className="pod-hero-sub">
            No jargon. Just smart, surprising stories..<br />about the future
          </p>
        </div>
      </div>
      <div className="pod-listen-bar">
        <div className="pod-inner pod-listen-inner">
          <p className="pod-listen-title">Listen &amp; Subscribe</p>
          <div className="pod-listen-logos">
            {/* External — target="_blank" */}
            <a href="https://www.youtube.com/@EnergizingIndiaPodcast" target="_blank" rel="noreferrer" className="pod-platform">
              <img src={IconYoutube} alt="YouTube" />
            </a>
            <a href="https://open.spotify.com/show/31CpjZm9DyfCR7sstBZlIf" target="_blank" rel="noreferrer" className="pod-platform">
              <img src={IconSpotify} alt="Spotify" />
            </a>
            <a href="https://podcasts.apple.com/in/podcast/energizing-india-podcast/id1530917254" target="_blank" rel="noreferrer" className="pod-platform">
              <img src={IconApplePodcast} alt="Apple Podcasts" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 2: EPISODES SLIDER
   Draggable: pointer events on wrap, threshold 60px
   Auto-play: 3500ms, resets on any interaction
───────────────────────────────────────────── */
function Episodes() {
  const getVisible = () => {
    if (window.innerWidth <= 480) return 1;
    if (window.innerWidth <= 768) return 2;
    return 3;
  };

  const [visible, setVisible] = useState(getVisible());
  const maxDot = EPISODES.length - visible;
  const [slide, setSlide] = useState(0);
  const timerRef    = useRef(null);
  const trackRef    = useRef(null);
  const wrapRef     = useRef(null);
  const dragStart   = useRef(null);
  const dragCurrent = useRef(0);
  const isDragging  = useRef(false);

  /* ── resize listener ── */
  useEffect(() => {
    const handler = () => {
      const v = getVisible();
      setVisible(v);
      setSlide(0); // reset to first slide on resize
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  /* ── auto-play ── */
  const startAuto = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSlide((s) => (s >= EPISODES.length - visible ? 0 : s + 1));
    }, 3500);
  };

  useEffect(() => {
    startAuto();
    return () => clearInterval(timerRef.current);
  }, [visible]);

  /* ── dot / programmatic go-to ── */
  const goTo = (i) => {
    setSlide(i);
    startAuto();
  };

  /* ── track offset per slide ── */
  const getTrackOffset = (s) => {
    if (!trackRef.current) return 0;
    const totalGaps = visible - 1;
    const cardW = (trackRef.current.offsetWidth - totalGaps * 30) / visible;
    return s * (cardW + 30);
  };

  /* sync transform when slide or visible changes */
  useEffect(() => {
    if (trackRef.current) {
      trackRef.current.style.transition = "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)";
      trackRef.current.style.transform = `translateX(-${getTrackOffset(slide)}px)`;
    }
  }, [slide, visible]);

  /* ── pointer drag handlers ── */
  /* ── shared logic ── */
  const handleDragStart = (clientX) => {
    isDragging.current  = false;
    dragStart.current   = clientX;
    dragCurrent.current = 0;
    wrapRef.current?.classList.add("pod-dragging");
    if (trackRef.current) trackRef.current.style.transition = "none";
    clearInterval(timerRef.current);
  };

  const handleDragMove = (clientX) => {
    if (dragStart.current === null) return;
    dragCurrent.current = clientX - dragStart.current;
    isDragging.current  = Math.abs(dragCurrent.current) > 5;
    if (isDragging.current && trackRef.current) {
      trackRef.current.style.transform =
        `translateX(${-getTrackOffset(slide) + dragCurrent.current}px)`;
    }
  };

  const handleDragEnd = () => {
    wrapRef.current?.classList.remove("pod-dragging");
    const currentMaxDot = EPISODES.length - visible;
    if (isDragging.current) {
      const threshold = 60;
      if (dragCurrent.current < -threshold && slide < currentMaxDot) {
        goTo(slide + 1);
      } else if (dragCurrent.current > threshold && slide > 0) {
        goTo(slide - 1);
      } else {
        if (trackRef.current) {
          trackRef.current.style.transition = "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)";
          trackRef.current.style.transform  = `translateX(-${getTrackOffset(slide)}px)`;
        }
        startAuto();
      }
    } else {
      startAuto();
    }
    dragStart.current   = null;
    dragCurrent.current = 0;
    isDragging.current  = false;
  };

  /* ── pointer (desktop) ── */
  const onPointerDown = (e) => {
    if (e.pointerType === "touch") return;
    handleDragStart(e.clientX);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup",   onPointerUp);
  };
  const onPointerMove = (e) => handleDragMove(e.clientX);
  const onPointerUp   = ()  => {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup",   onPointerUp);
    handleDragEnd();
  };

  /* ── touch (mobile/tablet) ── */
  const onTouchStart = (e) => handleDragStart(e.touches[0].clientX);
  const onTouchMove  = (e) => { e.preventDefault(); handleDragMove(e.touches[0].clientX); };
  const onTouchEnd   = ()  => handleDragEnd();

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    el.addEventListener("touchstart", onTouchStart, { passive: true  });
    el.addEventListener("touchmove",  onTouchMove,  { passive: false });
    el.addEventListener("touchend",   onTouchEnd,   { passive: true  });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove",  onTouchMove);
      el.removeEventListener("touchend",   onTouchEnd);
    };
  }, [slide, visible]);

  useEffect(() => {
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup",   onPointerUp);
    };
  }, []);
  return (
    <section className="pod-episodes">
      <div className="pod-inner">
        <div className="pod-ep-top pod-fade-up">
          <div>
            <p className="pod-ep-label">Favourite Podcast</p>
            <h2 className="pod-ep-title">Our Most Downloaded Episode Ever!</h2>
          </div>
          <a href="/all-episodes/" className="pod-btn-purple">
            Explore More Episodes
          </a>
        </div>
        <div
          ref={wrapRef}
          className="pod-slider-wrap pod-fade-up pod-delay-2"
          onPointerDown={onPointerDown}
        >
          <div ref={trackRef} className="pod-slider-track">
            {EPISODES.map((e, i) => <EpisodeCard key={i} {...e} />)}
          </div>
        </div>
        <div className="pod-ep-dots">
          {Array.from({ length: EPISODES.length - visible + 1 }).map((_, i) => (
            <button
              key={i}
              className={`pod-ep-dot${slide === i ? " pod-active" : ""}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 3: ABOUT
───────────────────────────────────────────── */
function About() {
  const stats = [
    { num: "70+",  label: "Episodes" },
    { num: "20k+", label: "Downloads" },
    { num: "80+",  label: "Expert Guest" },
    { num: "10+",  label: "Countries" },
  ];
  return (
    <section className="pod-about">
      <div className="pod-inner pod-about-row">
        <div className="pod-about-stats">
          {stats.map((s, i) => <AnimatedStat key={i} num={s.num} label={s.label} />)}
        </div>
        <div className="pod-about-logo-col pod-fade-up pod-delay-2">
          <img src={AboutLogo} alt="Energizing India" />
        </div>
        <div className="pod-about-text-col pod-fade-up pod-delay-3">
          <p className="pod-about-eyebrow">About Us</p>
          <h2 className="pod-about-title">Connecting Through<br />Stories &amp; Sound</h2>
          <p className="pod-about-body">
            Energizing India cuts through the noise of the clean energy conversation.
            We go beyond headlines and jargon to deliver authentic dialogues with the
            pioneers building India's sustainable future, from visionary founders and
            leading technologists to the policymakers shaping our national strategy.
            <br />
            Our format is intentionally unscripted and insightful, fostering a space
            for genuine exploration. Inspired by the depth of international standards
            like ABC Australia, our conversations are respectful yet candid, designed
            to uncover not just what our guests are doing, but why it matters.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 4: SUBSCRIBE (parallax)
───────────────────────────────────────────── */
function Subscribe() {
  const [email, setEmail] = useState("");
  return (
    <section
      className="pod-subscribe"
      style={{ backgroundImage: `url(${SubscribeBg})` }}
    >
      <div className="pod-subscribe-overlay" />
      <div className="pod-subscribe-content">
        <div className="pod-inner">
          <h2 className="pod-sub-title pod-fade-up">Subscribe for<br />Exclusive Content!</h2>
          <div className="pod-sub-form pod-fade-up pod-delay-2">
            <input
              className="pod-sub-input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button className="pod-sub-btn">
              Subscribe
              <img src={IconArrowRight} alt="" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 5: NEW EPISODE ALERT (2x2 Spotify grid)
───────────────────────────────────────────── */
function NewEpisodeAlert() {
  return (
    <section className="pod-newalert">
      <div className="pod-inner">
        <h2 className="pod-newalert__heading pod-fade-up">New Episode Alert!</h2>
        <div className="pod-newalert__grid">
          {SPOTIFY_EPISODES.map((ep, i) => (
            <SpotifyCard key={ep.id} id={ep.id} highlight={ep.highlight} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 6: QUOTE / WATERCOLOR BG
───────────────────────────────────────────── */
function QuoteSection() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
    <section className="pod-quote">
      <img src={isMobile ? AlertBgMobile : AlertBg} alt="" className="pod-quote__bg" aria-hidden="true" />
      <div className="pod-inner pod-quote__row">
        <h2 className="pod-quote__text pod-fade-up">
          "The Best Way<br />Out is always<br />through"
        </h2>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 7: NEWS & BLOGS
───────────────────────────────────────────── */
function Blogs() {
  return (
    <section className="pod-blogs">
      <div className="pod-inner">
        <div className="pod-blogs-header pod-fade-up">
          <h2 className="pod-blogs-title">News &amp; Blogs!</h2>
          <p className="pod-blogs-sub">
            Our blogs dig deeper into the stories, people, and ideas driving India's clean energy future.
          </p>
        </div>
        <div className="pod-blog-grid">
          {BLOGS.map((b, i) => (
            <div key={i} className={`pod-fade-up pod-delay-${i + 1}`}>
              <BlogCard {...b} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   ROOT
───────────────────────────────────────────── */
export default function PodcastHome() {
  useScrollFade();
  return (
    <>
      <style>{CSS}</style>
      <div className="pod-wrap">
        <Hero />
        <Episodes />
        <About />
        <Subscribe />
        <NewEpisodeAlert />
        <QuoteSection />
        <Blogs />
      </div>
    </>
  );
}